import React from 'react';
import PropTypes from 'prop-types';
import { FaCar, FaEdit, FaPlane, FaTaxi, FaTrain, FaTrashAlt } from 'react-icons/fa';
import { formatBookingDate, formatNumber, formatNumberCurrency } from 'helpers/utils';
import { Card, ActionDropdown } from 'components/common';
import BookingAttachment from '../../BookingAttachment';
import ShuttleUploadAttachmentDirectly from './ShuttleUploadAttachmentDirectly';

const ICON_MAP = { plane: FaPlane, train: FaTrain, taxi: FaTaxi };

const Field = ({ label, children }) => (
  <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-0.5 sm:tw-gap-2 tw-mb-1.5 tw-text-sm">
    <span className="sm:tw-w-44 sm:tw-flex-shrink-0 tw-font-semibold tw-text-slate-600">{label}</span>
    <span className="tw-text-blue-700 tw-min-w-0 tw-break-words">{children}</span>
  </div>
);
Field.propTypes = { label: PropTypes.node, children: PropTypes.node };

const ShuttleItem = ({ booking, shuttle, icon, editShuttleBooking, deleteShuttleBooking, handleRemoveAttachment }) => {
  const isCarOfCompany = shuttle && shuttle.name === 'Car of company';
  const isUSDCurrency = booking.currency === 'USD';
  const Icon = ICON_MAP[icon] || FaCar;

  const items = [];
  if (!isCarOfCompany) items.push({ label: 'Edit', icon: <FaEdit />, onClick: editShuttleBooking }, { divider: true });
  items.push({ label: 'Delete', icon: <FaTrashAlt />, danger: true, onClick: deleteShuttleBooking });

  return (
    <Card
      padding="none"
      className="tw-mb-4"
      header={(
        <div className="tw-flex tw-items-start tw-justify-between">
          <div>
            <div className="tw-text-base tw-font-semibold tw-uppercase tw-text-blue-700 tw-flex tw-items-center tw-gap-2"><Icon /> {shuttle.name} (ID#{shuttle.id})</div>
            <div className="tw-text-xs tw-text-slate-500 tw-mt-1">Created date: {shuttle.created_date} | by: {shuttle.created_by}</div>
          </div>
          <ActionDropdown items={items} />
        </div>
      )}
    >
      <div className="tw-p-4">
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-6">
          <div>
            <h6 className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-widest tw-text-slate-500 tw-mb-3">Shuttle Information</h6>
            <Field label="Shuttle name">{shuttle.name}</Field>
            <Field label="Departure date">{formatBookingDate(shuttle.departure_date)}</Field>
            <Field label="Return date">{formatBookingDate(shuttle.return_date)}</Field>
            <Field label="Primary"><input type="checkbox" readOnly checked={shuttle.primary} /></Field>
          </div>
          <div>
            <h6 className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-widest tw-text-slate-500 tw-mb-3">Expense</h6>
            {isCarOfCompany ? (
              <>
                <Field label="Amount actual currency">N/A</Field>
                <Field label="Amount actual">N/A</Field>
                <Field label="Car booking">Link here</Field>
              </>
            ) : (
              <>
                <Field label="Amount estimate currency">{formatNumberCurrency(shuttle.amount_estimate_currency, shuttle.currency)}</Field>
                <Field label="Amount estimate">{formatNumberCurrency(shuttle.amount_estimate, 'VND')}</Field>
                <hr className="tw-w-3/4 tw-border-slate-200" />
                <Field label="Amount actual currency">{formatNumberCurrency(shuttle.amount_currency, shuttle.currency)}</Field>
                <Field label="Amount actual">{formatNumberCurrency(shuttle.amount, 'VND')}</Field>
                <hr className="tw-w-3/4 tw-border-slate-200" />
                {isUSDCurrency && <Field label="Exchange rate">{formatNumber(shuttle.exchange_rate)} VND</Field>}
              </>
            )}
            <Field label="Note"><span dangerouslySetInnerHTML={{ __html: shuttle.notes }} /></Field>
          </div>
        </div>

        {!isCarOfCompany && (
          <div className="tw-mt-4">
            <h6 className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-widest tw-text-slate-500 tw-mb-3">Attachments</h6>
            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4">
              <ShuttleUploadAttachmentDirectly booking={booking} shuttle={shuttle} />
              <div className="tw-flex tw-flex-wrap tw-gap-1">
                {(shuttle.documents || []).map((attachment) => (
                  <BookingAttachment
                    {...attachment}
                    key={attachment.id}
                    handleRemoveAttachment={(documentId) => handleRemoveAttachment(shuttle.id, documentId)}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

ShuttleItem.propTypes = {
  booking: PropTypes.object.isRequired,
  shuttle: PropTypes.object.isRequired,
  icon: PropTypes.string.isRequired,
  editShuttleBooking: PropTypes.func.isRequired,
  deleteShuttleBooking: PropTypes.func.isRequired,
  handleRemoveAttachment: PropTypes.func.isRequired,
};

export default ShuttleItem;
